import { Box, Container, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormattedOwnerData } from "../../../core/models/ownerData/ownerData.model";
import "./SuppliesHeader.scss";
import theme from "../../../muiTheme";
import {
  TabsDataSuppliesHeader,
  TotalGroupedSupplies,
} from "../../../core/models/supplies/supplies.model";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import { SupplyEnviroment } from "../Supplies";

interface SuppliesHeaderProps {
  ownerData: FormattedOwnerData | null;
  isDesktopViewport: boolean;
  totalSupplies: TotalGroupedSupplies;
  envirormentSelected: SupplyEnviroment;
  setEnvirormentSelected: (enviroment: SupplyEnviroment) => void;
}

const SuppliesHeader = ({
  ownerData,
  isDesktopViewport,
  totalSupplies: {
    totalContratosGas,
    totalContratosLuz,
    totalRegistrosQuery,
    totalSuministrosGas,
    totalSuministrosLuz,
  },
  envirormentSelected,
  setEnvirormentSelected,
}: SuppliesHeaderProps) => {
  const { t } = useTranslation();

  const totalSupplies =
    (totalSuministrosLuz ? totalSuministrosLuz : 0) +
    (totalSuministrosGas ? totalSuministrosGas : 0);

  const suppliesClientsHeaderData = [
    {
      id: "supplies",
      label: `Suministros.${
        ownerData?.supplyNumber === "1" ? "Suministro" : "Suministros"
      }`,
      /* value: ownerData?.supplyNumber || 0, */
      value: totalSupplies,
      showCondition: true,
      classNameIcon: "t-icon-map",
    },
    {
      id: "clients",
      label: `Suministros.${
        ownerData?.entitiesNumber === "1" ? "Entidad" : "Entidades"
      }`,
      value: ownerData?.entitiesNumber || 0,
      showCondition: ownerData?.isAdministrator,
      classNameIcon: "t-icon-users",
    },
  ];

  const tabsDataSuppliesHeader: TabsDataSuppliesHeader[] = [
    {
      id: "light",
      value: "light",
      icon: "t-icon-electricite",
      label: "Luz",
      quantity: totalSuministrosLuz || 0,
      color: theme.palette.background.electricity,
      backgroundColor: "rgba(0,170,97,1)",
    },
    {
      id: "gas",
      value: "gas",
      icon: "t-icon-gaz",
      label: "Gas",
      quantity: totalSuministrosGas || 0,
      color: theme.palette.background.gas,
      backgroundColor: "rgba(70,50,255,1)",
    },
    // {
    //   id: "services",
    //   value: "services",
    //   icon: "t-icon-petrole",
    //   label: "Servicios",
    //   quantity: 0,
    //   color: theme.palette.background.services,
    // },
  ];

  return (
    <Stack
      direction="column"
      rowGap={{ xs: "24px", md: "40px" }}
      component="article"
      className="supplies-header"
    >
      <Container
        component="div"
        className="supplies-header__title-container"
        sx={{ display: "flex" }}
      >
        <Typography variant="h1" component="h1">
          {t("Header.Nav.Suministros")}
        </Typography>
        <Box
          component="div"
          className="supplies-header__supplies-clients-container"
          flexWrap={"wrap"}
        >
          {suppliesClientsHeaderData.map(
            ({ id, label, value, classNameIcon, showCondition }) =>
              showCondition && (
                <Box
                  key={id}
                  component="div"
                  className="supplies-header__supplies-clients"
                >
                  {/* Tag suministros */}
                  <span className={`${classNameIcon} supplies-header__icon`} />
                  <Typography component="p" variant="body1">
                    {formatNumber(+value)} {t(label)}
                  </Typography>
                </Box>
              )
          )}
        </Box>
      </Container>

      {!ownerData?.isAdministrator && (
        <Container
          component="div"
          className="supplies-header__title-container"
          sx={{ display: "flex" }}
        >
          <Box component="div" display="flex" alignItems="center" gap="0.75rem">
            <span className="t-icon-building brand-red" />
            <Typography variant="body1" component="p">
              {ownerData?.socialReason}
            </Typography>
            {isDesktopViewport && <span>|</span>}
            <Typography variant="body1" component="p">{`${t("Tablas.CIF")}: ${
              ownerData?.clientIdentification
            }`}</Typography>
          </Box>
        </Container>
      )}

      <Container
        component="div"
        className="supplies-header__title-container"
        sx={{ display: "flex" }}
      >
        <Box sx={{ width: "100%" }}>
          <Tabs
            TabIndicatorProps={{
              style: {
                backgroundColor: tabsDataSuppliesHeader.find(
                  ({ value }) => value === envirormentSelected
                )?.backgroundColor,
              },
            }}
            value={envirormentSelected}
            onChange={(
              _event: React.SyntheticEvent,
              newValue: SupplyEnviroment
            ) => {
              setEnvirormentSelected(newValue);
            }}
            component={"ul"}
            sx={{
              ".MuiTabs-indicator": {
                left: 0,
              },
              paddingLeft: 0,
            }}
          >
            {tabsDataSuppliesHeader.map(
              ({ id, label, icon, value, quantity, color }) => (
                <Tab
                  key={id}
                  component="a"
                  label={
                    <Typography component="span">
                      <Stack gap="8px" direction="row" alignItems="center">
                        <span className={icon} />
                        {isDesktopViewport &&
                          `${t("Tablas." + label)} (${formatNumber(quantity)})`}
                        {!isDesktopViewport &&
                          value === envirormentSelected &&
                          `${t("Tablas." + label)} (${formatNumber(quantity)})`}
                      </Stack>
                    </Typography>
                  }
                  sx={{
                    "&.Mui-selected": {
                      backgroundImage: color,
                      backgroundSize: "100%",
                      backgroundRepeat: "repeat",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    },

                    "&:hover": {
                      backgroundImage: color,
                      backgroundSize: "100%",
                      backgroundRepeat: "repeat",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    },
                  }}
                  value={value}
                  className="tab"
                  disableRipple
                />
              )
            )}
          </Tabs>
        </Box>
      </Container>
    </Stack>
  );
};

export default SuppliesHeader;
